import {
    createThunk,
    Person,
    createHubPersona,
    Feature,
    runWithQoS,
    getMatchingFavoritePerson,
    getLogger,
    PersonType,
    getOwsPersona,
    getNotesForPersona,
    updatePersona,
    UpdateProperty,
    createPersona,
    getOwsPersonaByEmail,
    ThunkDispatch,
    makeHubPersonaId,
    makeHubGroupPersonaId,
    contactsLeftNavItemId,
} from 'people/lib/shared';
import type { PersonaType } from 'people/lib/service';
import { getClientVersion, getSessionId } from 'owa-config';
import ContactsAPIClient, { Contact, convertContactToPersonType } from 'owa-contacts-api';
import {
    getLokiBootstrapperConfig,
    initializeLivePersonaCardAsync as initLpc,
    initializeLivePersonaEditorAsync,
    BootstrapperConfig,
} from 'owa-people-loki';
import { getSharedMailboxAadObjectId } from 'owa-people-loki/lib/utils/getSharedMailboxAadObjectId';
import sessionStore from 'owa-session-store/lib/store/store';
import {
    getAppState,
    getCurrentWritableContactFolderId,
    getFavorites,
    lpcPersonaCreatedAction,
    lpcPersonaUpdatedAction,
    livePersonaCardInitializeCompletedAction,
    livePersonaEditorInitializeCompletedAction,
    lpcGroupMembershipRequestApprovedAction,
    lpcFollowGroupCompletedAction,
    lpcUnFollowGroupCompletedAction,
    lpcAddGroupMembersClickedAction,
    lpcExpandedViewNavigationAction,
    EditContactOptionsState,
    lpcRenewGroupCompletedAction,
    lpcEditGroupClickedAction,
    lpcPromoteGroupMemberCompletedAction,
    lpcRemoveGroupMemberCompletedAction,
    lpcDemoteGroupMemberCompletedAction,
    lpcUpdateTransferOwnershipAction,
    getPackageState,
} from 'people/lib/appstate';
import getTokenForImageApi from 'owa-persona/lib/utils/getTokenForImageApi';
import onPersonaUpdated from 'owa-persona/lib/services/onPersonaUpdated';
import getPersonaPhotoUrl, {
    getPersonaPhotoUrlFromhexCID,
} from 'owa-persona/lib/utils/getPersonaPhotoUrl';
import getHexConsumerIdForUser from 'owa-persona/lib/actions/getHexConsumerIdForUser';
import { PersonaSize } from '@fluentui/react/lib/Persona';
import type { HostAppProvidedPersonaIdentifiers } from 'owa-people-loki/lib/models/models';
import {
    addCurrentPersonClicked,
    editPersonaClickedWithPersonaId,
    lpcApproveMembershipRequestNotification,
    lpcLeaveGroupCompleted,
    lpcRenewGroup,
    lpcDemoteSelfCompleted,
    lpcJoinGroupCompleted,
    lpcJoinGroupError,
    HubState,
    packageStateKey,
    getCurrentHubPersonaWritableContactFolderId,
    getPublicFolderForCurrentHubPersona,
    contactEditorNewContactSaveClickedAction,
} from 'people/lib/hub';
import { prefetchInitialGroupIds } from 'people/lib/hub/actions/data/groups/unifiedGroupActions';
import {
    setLpcGroupSubscriptionUpdateListener,
    setLpcGroupDataUpdateListener,
    setLpcGroupMembersDataUpdateListener,
    setLpcGroupRenewalUpdateListener,
    setLpcGroupDeleteListener,
    setLpcGroupUpdateSmtpListener,
} from './middleware/lpcDataUpdateMiddleware';
import { mailComposeClicked } from 'people/lib/editors/actions';
import { addPopoutV2, getReadingPaneRouteForPopoutV2 } from 'owa-popout-v2';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getGuid } from 'owa-guid';
import { owsPersonaToLpcPerson } from 'owa-people-loki/lib/utils/owsPersonaToLpcPerson';

const toLpcRequestId = (options: { requestId: string }) => ({ lpcRequestId: options.requestId });

const getOwaMailFavoritesStore = () => import(/*OwaMailFavoritesStore*/ 'owa-mail-favorites-store');

export const initializeLivePersonaCard = createThunk(
    'InitializeLivePersonaCard',
    async (dispatch: ThunkDispatch, getState: () => {}) => {
        const callbacks = {
            composeMail: (recipient: string, subject?: string, body?: string) => {
                let personaArr = [];
                if (recipient) {
                    const persona = {
                        type: PersonType.Person,
                        info: {
                            mainEmailAddress: recipient,
                            originalDisplayName: recipient,
                        },
                    } as Person;
                    personaArr = [persona];
                }

                mailComposeClicked(dispatch)(personaArr, 'immersiveProfile', subject, body);
            },
            readMail: (mailId: string) => {
                popoutReadingPane(mailId);
            },
            addContact: (
                olsPersonaId: string,
                _completedCallback: (olsPersonaId?: string, error?: string) => void
            ) => dispatch(addCurrentPersonClicked('EV')),
            editContact: (
                olsPersonaId: string,
                _completedCallback: (olsPersonaId?: string, error?: string) => void,
                options?: EditContactOptionsState
            ) => dispatch(editPersonaClickedWithPersonaId('EV', olsPersonaId)),
            onPersonaCreated: (personaType: PersonaType | Contact) => {
                const state = getPackageState<HubState>(getState(), packageStateKey);

                // Determine if type Contact
                if ('id' in personaType) {
                    const hubContact = convertContactToPersonType(personaType);
                    // The scenario is Merge contact so we call the contact editor actions
                    dispatch(
                        contactEditorNewContactSaveClickedAction({
                            personaId: hubContact?.personaId,
                            folderId: contactsLeftNavItemId,
                            hubContact,
                        })
                    );
                } else {
                    dispatch(
                        lpcPersonaCreatedAction({
                            persona: createHubPersona(personaType),
                            folderId: getCurrentHubPersonaWritableContactFolderId(
                                state.appState,
                                state.packageState
                            ),
                        })
                    );
                }
            },
            onPersonaUpdated: (
                personaType: PersonaType,
                updatedProperties: { profilePhotoDataUrl?: string }
            ) => {
                const persona = createHubPersona(personaType);

                if (persona.type === PersonType.Person) {
                    // If we have updated a favorite, we need to refresh the contents of the userConfiguration to reflect the changes
                    const favoritePeople = getFavorites(getAppState(getState()));
                    const matchingFavorite = getMatchingFavoritePerson(
                        persona.personaId,
                        persona.info.mainEmailAddress,
                        favoritePeople
                    );
                    if (matchingFavorite && !isFeatureEnabled('tri-favorites-roaming')) {
                        getOwaMailFavoritesStore().then(module =>
                            module.lazyOnFavoritePersonaUpdated
                                .importAndExecute(matchingFavorite.favoriteId)
                                .catch(e =>
                                    getLogger().log('OnFavoritePersonaUpdatedError', {
                                        errorMessage: e.message,
                                    })
                                )
                        );
                    }

                    dispatch(lpcPersonaUpdatedAction({ persona }));
                    if (updatedProperties?.profilePhotoDataUrl) {
                        onPersonaUpdated(personaType, {
                            profilePhotoDataUrl: updatedProperties.profilePhotoDataUrl,
                        });
                    }
                }
            },
            toggleSubscribeToGroup: (
                hostAppPersonaIdentifiers: HostAppProvidedPersonaIdentifiers,
                newSubscribeState: boolean
            ) => {
                dispatch(
                    newSubscribeState
                        ? lpcFollowGroupCompletedAction({
                              groupId: hostAppPersonaIdentifiers.AadObjectId,
                              hubPersonaId: makeHubGroupPersonaId(
                                  hostAppPersonaIdentifiers.AadObjectId,
                                  hostAppPersonaIdentifiers.Smtp
                              ),
                              isFollowed: newSubscribeState,
                          })
                        : lpcUnFollowGroupCompletedAction({
                              groupId: hostAppPersonaIdentifiers.AadObjectId,
                              hubPersonaId: makeHubGroupPersonaId(
                                  hostAppPersonaIdentifiers.AadObjectId,
                                  hostAppPersonaIdentifiers.Smtp
                              ),
                              isFollowed: newSubscribeState,
                          })
                );
            },
            editGroup: () => {
                dispatch(lpcEditGroupClickedAction());
            },
            leaveGroup: (hostAppPersonaIdentifiers: HostAppProvidedPersonaIdentifiers) => {
                dispatch(
                    lpcLeaveGroupCompleted(
                        makeHubGroupPersonaId(
                            hostAppPersonaIdentifiers.AadObjectId,
                            hostAppPersonaIdentifiers.Smtp
                        )
                    )
                );
            },
            updateMembership: (
                hostAppPersonaIdentifiers: HostAppProvidedPersonaIdentifiers,
                groupMembershipScenarios:
                    | 'RemoveGroupMember'
                    | 'PromoteGroupMember'
                    | 'DemoteGroupOwner'
                    | 'AddGroupMember',
                successCount: number,
                failedCount: number,
                isSelf: boolean
            ) => {
                if (successCount > 0) {
                    switch (groupMembershipScenarios) {
                        case 'PromoteGroupMember':
                            dispatch(
                                lpcPromoteGroupMemberCompletedAction({
                                    groupId: hostAppPersonaIdentifiers.AadObjectId,
                                    promotedMembers: successCount,
                                    hubPersonaId: makeHubGroupPersonaId(
                                        hostAppPersonaIdentifiers.AadObjectId,
                                        hostAppPersonaIdentifiers.Smtp
                                    ),
                                })
                            );
                            break;
                        case 'RemoveGroupMember':
                            if (isSelf) {
                                dispatch(
                                    lpcLeaveGroupCompleted(
                                        makeHubGroupPersonaId(
                                            hostAppPersonaIdentifiers.AadObjectId,
                                            hostAppPersonaIdentifiers.Smtp
                                        )
                                    )
                                );
                            } else {
                                dispatch(
                                    lpcRemoveGroupMemberCompletedAction({
                                        groupId: hostAppPersonaIdentifiers.AadObjectId,
                                        removedMembers: successCount,
                                        hubPersonaId: makeHubGroupPersonaId(
                                            hostAppPersonaIdentifiers.AadObjectId,
                                            hostAppPersonaIdentifiers.Smtp
                                        ),
                                    })
                                );
                            }
                            break;
                        case 'DemoteGroupOwner':
                            dispatch(
                                lpcDemoteGroupMemberCompletedAction({
                                    groupId: hostAppPersonaIdentifiers.AadObjectId,
                                    demotedMembers: successCount,
                                    hubPersonaId: makeHubGroupPersonaId(
                                        hostAppPersonaIdentifiers.AadObjectId,
                                        hostAppPersonaIdentifiers.Smtp
                                    ),
                                })
                            );
                            if (isSelf) {
                                dispatch(
                                    lpcDemoteSelfCompleted(
                                        hostAppPersonaIdentifiers.AadObjectId,
                                        makeHubGroupPersonaId(
                                            hostAppPersonaIdentifiers.AadObjectId,
                                            hostAppPersonaIdentifiers.Smtp
                                        )
                                    )
                                );
                            }
                            break;
                        case 'AddGroupMember':
                            if (isSelf) {
                                dispatch(
                                    lpcJoinGroupCompleted(
                                        makeHubGroupPersonaId(
                                            hostAppPersonaIdentifiers.AadObjectId,
                                            hostAppPersonaIdentifiers.Smtp
                                        )
                                    )
                                );
                            }
                            break;
                        default:
                    }
                } else {
                    switch (groupMembershipScenarios) {
                        case 'AddGroupMember':
                            if (isSelf) {
                                dispatch(
                                    lpcJoinGroupError(
                                        makeHubGroupPersonaId(
                                            hostAppPersonaIdentifiers.AadObjectId,
                                            hostAppPersonaIdentifiers.Smtp
                                        )
                                    )
                                );
                            }
                            break;
                        default:
                    }
                }
            },
            addGroupMembers: (groupSmtp: string) => {
                dispatch(lpcAddGroupMembersClickedAction());
            },
            onApproveGroupMembershipRequests: (
                hostAppPersonaIdentifiers: HostAppProvidedPersonaIdentifiers,
                membershipRequestsCount: number,
                failedMembershipRequestsCount: number
            ) => {
                dispatch(
                    lpcGroupMembershipRequestApprovedAction({
                        hubPersonaId: makeHubGroupPersonaId(
                            hostAppPersonaIdentifiers.AadObjectId,
                            hostAppPersonaIdentifiers.Smtp
                        ),
                        membershipRequestsCount,
                        failedMembershipRequestsCount,
                    })
                );
                lpcApproveMembershipRequestNotification(
                    makeHubGroupPersonaId(
                        hostAppPersonaIdentifiers.AadObjectId,
                        hostAppPersonaIdentifiers.Smtp
                    ),
                    membershipRequestsCount,
                    failedMembershipRequestsCount
                );
            },
            renewGroup: (hostAppPersonaIdentifiers: HostAppProvidedPersonaIdentifiers) => {
                dispatch(
                    lpcRenewGroupCompletedAction({
                        groupId: hostAppPersonaIdentifiers.AadObjectId,
                        groupSmtp: hostAppPersonaIdentifiers.Smtp,
                    })
                );
                dispatch(
                    lpcRenewGroup(
                        makeHubGroupPersonaId(
                            hostAppPersonaIdentifiers.AadObjectId,
                            hostAppPersonaIdentifiers.Smtp
                        )
                    )
                );
            },
            onCancelTransferOwnership: (
                group: HostAppProvidedPersonaIdentifiers,
                isTransferOwnershipDialog: boolean
            ) => {
                dispatch(
                    lpcUpdateTransferOwnershipAction({
                        unifiedGroupId: group.AadObjectId,
                        isTransferOwnershipDialog,
                    })
                );
            },
        };

        const onExpandedViewNavigation = (targetPersona: {
            identifiers: HostAppProvidedPersonaIdentifiers;
            isNavigateBackAvailable: boolean;
            displayName?: string;
            firstName?: string;
            lastName?: string;
        }) => {
            // https://msfast.visualstudio.com/FAST/_workitems/edit/250070
            // The information on the new user will come from LPC, until then we have to issue a request

            // Simplifying the Flow here and fix major navigation issues on PeopleHub.
            // Fetch OwsPersonaId if email is present regardless of what other information we get.
            // This removes any cases where email is present and OldPersonId is not present.
            const hasNamePresent =
                targetPersona.displayName || (targetPersona.firstName && targetPersona.lastName);

            if (targetPersona.identifiers.Smtp) {
                getOwsPersonaByEmail(targetPersona.identifiers.Smtp).then(
                    persona => {
                        dispatch(
                            lpcExpandedViewNavigationAction.success({
                                persona: createHubPersona(persona),
                                isNavigateBackAvailable: targetPersona.isNavigateBackAvailable,
                            })
                        );
                    },
                    error => dispatch(lpcExpandedViewNavigationAction.error({ error: error }))
                );
            } else if (hasNamePresent) {
                const hubPersona: Person = {
                    type: PersonType.Person,
                    personaId: null,
                    hubPersonaId: makeHubPersonaId(
                        targetPersona.identifiers.OlsPersonaId ||
                            targetPersona.identifiers.AadObjectId ||
                            targetPersona.identifiers.Smtp
                    ),
                    info: {
                        originalDisplayName: targetPersona.displayName,
                        allEmailAddresses: [targetPersona.identifiers.Smtp],
                        displayNameLastFirst: targetPersona.displayName,
                        displayNameFirstLast: targetPersona.displayName,
                        mainEmailAddress: targetPersona.identifiers.Smtp,
                        givenName: targetPersona.firstName,
                        surname: targetPersona.lastName,
                    },
                };
                dispatch(
                    lpcExpandedViewNavigationAction.success({
                        persona: hubPersona,
                        isNavigateBackAvailable: targetPersona.isNavigateBackAvailable,
                    })
                );
            }
        };

        const actionCallbacks = { ...callbacks, onExpandedViewNavigation };
        let token = await getTokenForImageApi();

        const dataCallbacks = {
            getAndPrefetchPersonaImageUri: (
                personaId: {
                    Smtp?: string;
                    OlsPersonaId?: string;
                    AadObjectId?: string;
                    PersonaType?: string;
                },
                callback: (
                    uri?: string,
                    headers?: { [key in string]: string },
                    error?: string
                ) => void
            ) => {
                const hexCid: string = getHexConsumerIdForUser(personaId.Smtp);
                let headers = {};
                if (token) {
                    headers = {
                        Authorization: `Bearer ${token}`,
                        'X-AnchorMailbox': personaId.Smtp,
                    };
                }
                const mailboxType = personaId.PersonaType == 'Group' ? 'GroupMailbox' : undefined;
                callback(
                    hexCid
                        ? getPersonaPhotoUrlFromhexCID(hexCid, mailboxType, PersonaSize.regular)
                        : getPersonaPhotoUrl(
                              personaId.Smtp,
                              personaId.OlsPersonaId,
                              mailboxType,
                              true
                          ),
                    headers
                );
            },
            getPersonaInfo: (
                personaId: { Smtp?: string; OlsPersonaId?: string; AadObjectId?: string },
                callback: (data: any, error: string) => void
            ) => {
                const state = getPackageState<HubState>(getState(), packageStateKey);
                const publicFolder = getPublicFolderForCurrentHubPersona(
                    state.appState,
                    state.packageState
                );

                // REST APIs do not support public folders (yet), so using OWS GetPersona
                if (publicFolder) {
                    Promise.all([
                        getOwsPersona(personaId.OlsPersonaId, publicFolder.id, null),
                        getNotesForPersona(personaId.OlsPersonaId, publicFolder.id, null),
                    ]).then(result => {
                        const personalNotes = result[1].BodiesArray.map(note => ({
                            Notes: note.Value.Value,
                            SourceId: '',
                            Provenance: '',
                        }));

                        callback({ ...owsPersonaToLpcPerson(result[0]), personalNotes }, undefined);
                    });
                } else {
                    callback(undefined, undefined);
                }
            },
        };

        const privateDataCallbacks = {
            updateOwsPersona: (
                personaId: string,
                properties: UpdateProperty[],
                options: { requestId: string }
            ): Promise<PersonaType> => {
                if (properties && properties.length > 0) {
                    const state = getPackageState<HubState>(getState(), packageStateKey);

                    return updatePersona(
                        personaId,
                        getCurrentHubPersonaWritableContactFolderId(
                            state.appState,
                            state.packageState
                        ),
                        properties,
                        toLpcRequestId(options)
                    );
                } else {
                    return Promise.resolve(undefined);
                }
            },
            getOwsPersona: (personaId: string, options: { requestId: string }) => {
                const state = getPackageState<HubState>(getState(), packageStateKey);

                return getOwsPersona(
                    personaId,
                    getCurrentHubPersonaWritableContactFolderId(state.appState, state.packageState),
                    toLpcRequestId(options)
                );
            },
            getNotesForPersona: (personaId: string, options: { requestId: string }) => {
                const state = getPackageState<HubState>(getState(), packageStateKey);

                return getNotesForPersona(
                    personaId,
                    getCurrentHubPersonaWritableContactFolderId(state.appState, state.packageState),
                    toLpcRequestId(options)
                );
            },
            createOwsPersona: (
                properties: { name: string; newValue: string; oldValue: string }[],
                options: { requestId: string }
            ) => {
                const state = getPackageState<HubState>(getState(), packageStateKey);

                return createPersona(
                    properties,
                    getCurrentWritableContactFolderId(state.appState),
                    toLpcRequestId(options)
                );
            },
        };

        const dataUpdateBroadcaster = {
            setGroupSubscriptionUpdateListener: setLpcGroupSubscriptionUpdateListener,
            setGroupUpdateListener: setLpcGroupDataUpdateListener,
            setGroupMembersUpdateListener: setLpcGroupMembersDataUpdateListener,
            setPresenceUpdateListener: () => {
                return {};
            },
            setHostAppCapabilitiesUpdateListener: () => {
                return {};
            },
            setGroupRenewalUpdateListener: setLpcGroupRenewalUpdateListener,
            setGroupDeleteListener: setLpcGroupDeleteListener,
            setGroupUpdateSmtpListener: setLpcGroupUpdateSmtpListener,
        };

        const bootstrapperConfig = getLokiBootstrapperConfig();
        const sharedMailboxAadObjectId = getSharedMailboxAadObjectId();
        const isSetPhotoEnabled: boolean =
            sessionStore.userConfiguration.SegmentationSettings.SetPhoto;

        const lpcBootstrapperConfig: BootstrapperConfig = {
            ...bootstrapperConfig,
            clientCorrelationId: getAndLogClientCorrelationId(),
            clientType: 'OwaPeopleHub',
            actionCallbacks,
            dataCallbacks,
            privateDataCallbacks,
            dataUpdateBroadcaster,
            options: {
                isSetPhotoEnabled: isSetPhotoEnabled,
                isImmersiveProfileEnabled: true,
                hideDocumentFilter: false,
                sharedMailboxAadObjectId,
            },
        };

        runWithQoS<void>({
            scenarioName: 'InitializeLPC',
            feature: Feature.LivePersonaCard,
            operation: initLpc(lpcBootstrapperConfig).then(
                () => {
                    dispatch(
                        livePersonaCardInitializeCompletedAction.success({
                            version: window['LPC'].version,
                        })
                    );
                    dispatch(prefetchInitialGroupIds());
                },
                error => {
                    dispatch(livePersonaCardInitializeCompletedAction.error());
                }
            ),
        });
    }
);

export const initializeLivePersonaEditor = createThunk(
    'InitializeLivePersonaEditor',
    (dispatch: ThunkDispatch) => {
        if (ContactsAPIClient.isEnabled()) {
            const bootstrapperConfig = getLokiBootstrapperConfig();
            const sharedMailboxAadObjectId = getSharedMailboxAadObjectId();
            const isSetPhotoEnabled: boolean =
                sessionStore.userConfiguration.SegmentationSettings.SetPhoto;
            const lpeBootstrapperConfig: BootstrapperConfig = {
                ...bootstrapperConfig,
                clientCorrelationId: getAndLogClientCorrelationId(),
                clientType: 'OwaPeopleHub',
                options: {
                    isSetPhotoEnabled: isSetPhotoEnabled,
                    isImmersiveProfileEnabled: true,
                    hideDocumentFilter: false,
                    sharedMailboxAadObjectId,
                },
            };
            runWithQoS<void>({
                scenarioName: 'InitializeLPE',
                feature: Feature.LivePersonaEditor,
                operation: initializeLivePersonaEditorAsync(lpeBootstrapperConfig)
                    .then(lokiConfig => {
                        let loadedLPEModule = window['LPE'];
                        return loadedLPEModule.initialize({
                            ...bootstrapperConfig,
                            refreshAuthToken: (
                                callback: (
                                    token: string | undefined,
                                    error: string | undefined,
                                    authRequestCorrelationId: string | undefined,
                                    tokenTtlInSeconds: number | undefined
                                ) => void
                            ) =>
                                lpeBootstrapperConfig.getAuthToken(
                                    (token: string, authRequestCorrelationId: string) =>
                                        callback(
                                            token,
                                            undefined,
                                            authRequestCorrelationId,
                                            undefined
                                        ),
                                    (errorMessage: string, authRequestCorrelationId: string) =>
                                        callback(
                                            undefined,
                                            errorMessage,
                                            authRequestCorrelationId,
                                            undefined
                                        )
                                ),
                            clientType: 'OwaPeopleHub',
                            cultureName: lpeBootstrapperConfig.culture || '',
                            hostAppRing: lpeBootstrapperConfig.region,
                            hostAppName: 'OwaPeopleHub',
                            hostAppVersion: getClientVersion(),
                            lokiUrl: lokiConfig.LokiUrl || '',
                            clientCorrelationId: getAndLogClientCorrelationId(),
                            region: lokiConfig.LokiUrl,
                            accountType: lpeBootstrapperConfig.isConsumer ? 'Consumer' : 'Business',
                            complianceEnvironment: lpeBootstrapperConfig.complianceEnvironment,
                            lokiConfig,
                        });
                    })
                    .then(
                        () => {
                            dispatch(
                                livePersonaEditorInitializeCompletedAction.success({
                                    version: window['LPE'].version,
                                })
                            );
                        },
                        error => {
                            dispatch(livePersonaEditorInitializeCompletedAction.error());
                        }
                    ),
            });
        }
    }
);

function getAndLogClientCorrelationId(): string {
    const sessionId = getSessionId() || getGuid();

    // Log the correlationid mapping
    getLogger().log('LpcClientCorrelationIdReceived', {
        clientCorrelationId: sessionId,
    });

    return sessionId;
}

function popoutReadingPane(itemId: string) {
    const route = getReadingPaneRouteForPopoutV2(null /*mailboxInfo*/, itemId);
    addPopoutV2(
        'mail',
        route,
        null /*folderInfo*/,
        {
            resizable: true,
        },
        true /*skipOptinCheck*/
    );
}
export type { DataActionFactory } from 'people/lib/shared';
export type { Action } from 'people/lib/shared';
